import React, { useEffect, useRef, useState } from "react";
import {
  Form,
  Input,
  DatePicker,
  TimePicker,
  Row,
  Col,
  Typography,
  Button,
  List,
} from "antd";
import {
  UploadOutlined,
  FolderOpenOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import SelectReadingsModal from "../../../../../../Common/SelectReadingsModal";
import AttachmentsRecordings, {
  RecordingAttachment,
} from "./AttachmentsRecordings";
import AttachmentsCharts, { ChartAttachment } from "./AttachmentsCharts";

const { TextArea } = Input;

interface Reading {
  id: string;
  name: string;
  type: string;
}

interface MeetingFormProps {
  isPast?: boolean;
  initialReadings?: Reading[];
  initialRecordings?: RecordingAttachment[];
  initialCharts?: ChartAttachment[];
  onReadingsChange?: (readings: Reading[]) => void;
  onRecordingsChange?: (recordings: RecordingAttachment[]) => void;
  onChartsChange?: (charts: ChartAttachment[]) => void;
}

export default function MeetingForm({
  isPast = false,
  initialReadings = [],
  initialRecordings = [],
  initialCharts = [],
  onReadingsChange,
  onRecordingsChange,
  onChartsChange,
}: MeetingFormProps): React.ReactElement {
  const form = Form.useFormInstance();
  const [selectReadingsOpen, setSelectReadingsOpen] = useState(false);

  const [displayReadings, setDisplayReadings] =
    useState<Reading[]>(initialReadings);
  const [newReadingIds, setNewReadingIds] = useState<string[]>([]);
  const [displayRecordings, setDisplayRecordings] =
    useState<RecordingAttachment[]>(initialRecordings);
  const [displayCharts, setDisplayCharts] =
    useState<ChartAttachment[]>(initialCharts);

  const isInitialized = useRef(false);
  useEffect(() => {
    if (!isInitialized.current && initialReadings.length > 0) {
      isInitialized.current = true;
      setDisplayReadings(initialReadings);
    }
  }, [initialReadings]);

  useEffect(() => {
    if (!isInitialized.current && initialCharts.length > 0) {
      isInitialized.current = true;
      setDisplayCharts(initialCharts);
    }
  }, [initialCharts]);

  useEffect(() => {
    if (!isInitialized.current && initialRecordings.length > 0) {
      isInitialized.current = true;
      setDisplayRecordings(initialRecordings);
    }
  }, [initialRecordings]);
  // useEffect(() => {
  //   setDisplayRecordings(initialRecordings);
  // }, [initialRecordings]);

  // useEffect(() => {
  //   setDisplayReadings(initialReadings);
  // }, [initialReadings]);

  // useEffect(() => {
  //   setDisplayCharts(initialCharts);
  // }, [initialCharts]);

  // useEffect(() => {
  //   setDisplayRecordings(initialRecordings);
  // }, [initialRecordings]);

  const handleChartsChange = (charts: ChartAttachment[]) => {
    setDisplayCharts(charts);
    onChartsChange?.(charts);
    form.setFieldsValue({ charts });
  };

  // ─── READINGS ───
  const handleRemoveReading = (id: string) => {
    const updated = displayReadings.filter((r) => r.id !== id);
    const updatedIds = newReadingIds.filter((rid) => rid !== id);
    setDisplayReadings(updated);
    setNewReadingIds(updatedIds);
    onReadingsChange?.(updated);
    form.setFieldsValue({
      selectedReadings: updated,
      importFromLibraryReadings: updatedIds.map((id) => ({ id })),
    });
  };

  const handleAddReadings = (newReadings: any[]) => {
    const formatted = newReadings.map((r) => ({
      id: r.id,
      name: r.name,
      type: r.type,
    }));
    const updated = [...displayReadings, ...formatted];
    const updatedIds = [...newReadingIds, ...formatted.map((r) => r.id)];
    setDisplayReadings(updated);
    setNewReadingIds(updatedIds);
    onReadingsChange?.(updated);
    form.setFieldsValue({
      selectedReadings: updated,
      importFromLibraryReadings: updatedIds.map((id) => ({ id })),
    });
    setSelectReadingsOpen(false);
  };

  // ─── RECORDINGS ───
  const handleRecordingsChange = (recordings: RecordingAttachment[]) => {
    setDisplayRecordings(recordings);
    onRecordingsChange?.(recordings);
    form.setFieldsValue({ recordings });
  };

  // ─── TIME ───
  // End Time must always be after Start Time. When the picked Start Time is
  // not before the current End Time, auto-shift End Time to Start Time + 1h.
  const handleStartTimeChange = (start: dayjs.Dayjs | null) => {
    if (!start) return;
    const endTime = form.getFieldValue("endTime") as dayjs.Dayjs | null;
    if (!endTime || !start.isBefore(endTime)) {
      form.setFieldsValue({ endTime: start.add(1, "hour") });
    }
  };

  return (
    <>
      {/* Meeting Name + Date */}
      <Row gutter={16}>
        <Col span={12}>
          <Form.Item
            label="Meeting Name"
            name="name"
            rules={[{ required: true, message: "Meeting name is required" }]}
          >
            <Input placeholder="Type here" />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            label="Date"
            name="date"
            rules={[{ required: true, message: "Date is required" }]}
          >
            <DatePicker
              placeholder="Select"
              format="MMM DD, YYYY"
              style={{ width: "100%" }}
              disabled={isPast}
              disabledDate={(current) => {
                return current && current.isBefore(dayjs().startOf("day"));
              }}
            />
          </Form.Item>
        </Col>
      </Row>

      {/* Start Time + End Time */}
      <Row gutter={16}>
        <Col span={12}>
          <Form.Item
            label="Start Time"
            name="startTime"
            rules={[{ required: true }]}
          >
            <TimePicker
              placeholder="Select"
              format="h:mm A"
              minuteStep={5}
              style={{ width: "100%" }}
              use12Hours
              disabled={isPast}
              onChange={handleStartTimeChange}
              classNames={{ popup: "meeting-time-picker-popup" }}
            />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            label="End Time"
            name="endTime"
            dependencies={["startTime"]}
            rules={[
              { required: true },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  const start = getFieldValue(
                    "startTime",
                  ) as dayjs.Dayjs | null;
                  if (!value || !start || start.isBefore(value)) {
                    return Promise.resolve();
                  }
                  return Promise.reject(
                    new Error("End Time must be after Start Time"),
                  );
                },
              }),
            ]}
          >
            <TimePicker
              placeholder="Select"
              format="h:mm A"
              minuteStep={5}
              style={{ width: "100%" }}
              use12Hours
              disabled={isPast}
              classNames={{ popup: "meeting-time-picker-popup" }}
            />
          </Form.Item>
        </Col>
      </Row>

      {/* Country + State */}
      <Row gutter={16}>
        <Col span={12}>
          <Form.Item
            label="Country"
            name="country"
            rules={[{ required: !isPast }]}
          >
            <Input placeholder={isPast ? "-" : "Type here"} disabled={isPast} />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item label="State" name="state" rules={[{ required: !isPast }]}>
            <Input placeholder={isPast ? "-" : "Type here"} disabled={isPast} />
          </Form.Item>
        </Col>
      </Row>

      {/* City + Address */}
      <Row gutter={16}>
        <Col span={12}>
          <Form.Item label="City" name="city" rules={[{ required: !isPast }]}>
            <Input placeholder={isPast ? "-" : "Type here"} disabled={isPast} />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            label="Address"
            name="address"
            rules={[{ required: !isPast }]}
          >
            <Input
              placeholder={
                isPast ? "-" : "Thornridge Cir. Syracuse, Connecticut 35624"
              }
              disabled={isPast}
            />
          </Form.Item>
        </Col>
      </Row>

      {isPast && (
        <Row gutter={16}>
          <Col span={24}>
            <Form.Item label="Description" name="description">
              <TextArea
                rows={4}
                placeholder="Type here"
                showCount
                maxLength={1000}
              />
            </Form.Item>
          </Col>
        </Row>
      )}

      <Form.Item name="charts" noStyle>
        <AttachmentsCharts
          value={displayCharts}
          onChange={handleChartsChange}
        />
      </Form.Item>

      {/* Attachments (Recordings) */}
      {isPast && (
        <Form.Item name="recordings" noStyle>
          <AttachmentsRecordings
            value={displayRecordings}
            onChange={handleRecordingsChange}
          />
        </Form.Item>
      )}

      {/* Attachments (Readings) */}
      <div
        style={{
          border: "1px solid #e8e8e8",
          borderRadius: 8,
          padding: 16,
          marginBottom: 16,
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div>
            <Typography.Text strong>Attachments (Readings)</Typography.Text>
            <br />
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              Supported formats: PDF, XLSX, .doc
            </Typography.Text>
          </div>
          <Button
            icon={<FolderOpenOutlined />}
            type="primary"
            ghost
            onClick={() => setSelectReadingsOpen(true)}
          >
            Select from Library
          </Button>
        </div>

        <Form.Item name="selectedReadings" hidden>
          <Input type="hidden" />
        </Form.Item>
        <Form.Item name="importFromLibraryReadings" hidden>
          <Input type="hidden" />
        </Form.Item>

        {displayReadings.length > 0 && (
          <div
            style={{
              border: "1px solid #f0f0f0",
              borderRadius: 8,
              padding: 16,
              marginTop: 12,
            }}
          >
            <Typography.Text style={{ marginBottom: 12, display: "block" }}>
              {displayReadings.length} file
              {displayReadings.length !== 1 ? "s" : ""} attached
            </Typography.Text>

            <List
              dataSource={displayReadings}
              renderItem={(item) => (
                <List.Item
                  style={{
                    padding: "8px 0",
                    borderBottom: "1px solid #f0f0f0",
                  }}
                  actions={[
                    <Button
                      key="delete"
                      type="text"
                      icon={<DeleteOutlined />}
                      onClick={() => handleRemoveReading(item.id)}
                      style={{ color: "#999" }}
                    />,
                  ]}
                >
                  <Typography.Text style={{ textDecoration: "underline" }}>
                    {item.name}
                    {item.type && `.${item.type}`}
                  </Typography.Text>
                </List.Item>
              )}
            />
          </div>
        )}

        <SelectReadingsModal
          open={selectReadingsOpen}
          onClose={() => setSelectReadingsOpen(false)}
          onAdd={handleAddReadings}
        />
      </div>
    </>
  );
}
